import Header from "../header";
import Footer from "../footer";
import DatePicker from "../../components/DatePicker";
import FileUpload from "../../components/FileUpload";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { searchHospitals, loadHospitalCategories, loadDiseases, saveDraft,fetchIdProofs} from "apiCalls/applyClaimService";
import { KSMART_BASE_URL } from "../../constants";
import { useLanguage } from "context/LanguageContext";

function maskAadhaar(val) {
    if (!val || val === "-") return "-";
    const digits = String(val).replace(/\D/g, "");
    if (digits.length === 12) return `XXXX XXXX ${digits.slice(8)}`;
    return "XXXX XXXX XXXX";
}

function DiseaseClaim(){
    const navigate = useNavigate();
    const { state } = useLocation();
    const { t } = useLanguage();
    const loan = state?.loan || {};
    const initialClaim = state?.freshClaim ? {} : (state?.claim || {});
    const [diseaseCategory, setDiseaseCategory] = useState(initialClaim.diseaseCategory || "")
    const [diseaseCategories, setDiseaseCategories] = useState([])
    const [loadingDiseaseCategories, setLoadingDiseaseCategories] = useState(false)
    const [dateOfIllness,setDateOfIllness] = useState(initialClaim.dateOfIllness || "")
    const [hospitalCategory, setHospitalCategory] = useState(initialClaim.hospitalCategory || "")
    const [hospitalCategories, setHospitalCategories] = useState([])
    const [loadingHospitalCategories, setLoadingHospitalCategories] = useState(false)
    const [medicalCertificateIssuedBy,setMedicalCertificateIssuedBy] = useState(initialClaim.medicalCertificateIssuedBy || "")
    const [dateOfMedicalCertificate,setDateOfMedicalCertificate] = useState(initialClaim.dateOfMedicalCertificate || "")
    const [hospitalId, setHospitalId] = useState(initialClaim.hospitalId || "")
    const [medicalCertificateFile, setMedicalCertificateFile] = useState(state?.files?.medicalCertificateFile || null)
    const [dobProofDocType, setDobProofDocType] = useState(initialClaim.dobProofDocType || "")
    const [dobProofDocFile, setDobProofDocFile] = useState(state?.files?.dobProofDocFile || null)
    const [dobProofFile, setDobProofFile] = useState(state?.files?.dobProofFile || null)
    const [anotherDocName, setAnotherDocName] = useState(initialClaim.anotherDocName || "")
    const [anotherDocFile, setAnotherDocFile] = useState(state?.files?.anotherDocFile || null)
    const [errors,setErrors] = useState({})
    const [declared,setDeclared] = useState(false)
    const [declarationError, setDeclarationError] = useState("")
    const [draftSaved,setDraftSaved] = useState(false)
    const [draftSaving,setDraftSaving] = useState(false)
    const [draftError,setDraftError] = useState("")
    const [draftId, setDraftId] = useState(state?.draftId || null)

    const getDobVerifyUrl = () => {
        let formattedDob = loan.dob || "";
        if (formattedDob.includes("/")) {
            const [dd, mm, yyyy] = formattedDob.split("/");
            formattedDob = `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
        }
        return `${KSMART_BASE_URL}/ui/cr/public/verify-certificate/${loan.birthCertificateNumber}/${formattedDob}`;
    };

    const [hospitalSuggestions, setHospitalSuggestions] = useState([])
    const [hospitalLoading, setHospitalLoading] = useState(false)
    const [hospitalSearchError, setHospitalSearchError] = useState("")
    const [showHospitalDropdown, setShowHospitalDropdown] = useState(false)
    const hospitalDebounceRef = useRef(null)
    const hospitalDropdownRef = useRef(null)
    // Tracks the last-seen hospitalCategory to detect real user-driven changes vs mount/Strict-Mode remounts.
    const prevHospitalCategoryRef = useRef(initialClaim.hospitalCategory ?? "")
    const [idProofTypes, setIdProofTypes] = useState([])
    const [loadingIdProofTypes, setLoadingIdProofTypes] = useState(false)

    const formatFileSize = (bytes) => bytes < 1024 * 1024
        ? `${(bytes / 1024).toFixed(1)} KB`
        : `${(bytes / (1024 * 1024)).toFixed(1)} MB`

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (hospitalDropdownRef.current && !hospitalDropdownRef.current.contains(e.target)) {
                setShowHospitalDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // When hospital category changes, cancel any pending search and reset the hospital field.
    // Comparing to prevHospitalCategoryRef instead of a mounted-flag handles both Strict Mode
    // double-invocation (same value → skip) and genuine user changes (different value → clear).
    useEffect(() => {
        if (hospitalCategory === prevHospitalCategoryRef.current) return;
        prevHospitalCategoryRef.current = hospitalCategory;
        if (hospitalDebounceRef.current) clearTimeout(hospitalDebounceRef.current);
        setHospitalSuggestions([]);
        setShowHospitalDropdown(false);
        setHospitalId("");
        setMedicalCertificateIssuedBy("");
    }, [hospitalCategory]);

    const handleHospitalSearch = (value) => {
        setMedicalCertificateIssuedBy(value);
        if (errors.medicalCertificateIssuedBy) setErrors(prev => ({ ...prev, medicalCertificateIssuedBy: "" }));

        if (hospitalDebounceRef.current) clearTimeout(hospitalDebounceRef.current);

        if (!value.trim()) {
            setHospitalId("");
            setHospitalSuggestions([]);
            setShowHospitalDropdown(false);
            return;
        }

        hospitalDebounceRef.current = setTimeout(async () => {
            setHospitalLoading(true);
            setHospitalSearchError("");
            try {
                const query = value.trim().toLowerCase();

                // Also try a dot-separated version for abbreviation-style input
                // e.g. "aj" → "a.j", so the API can find "A.J Hospital"
                const dottedQuery =
                    query.length >= 2 && query.length <= 6 && /^[a-z0-9]+$/.test(query)
                        ? query.split("").join(".")
                        : null;

                const [res1, res2] = await Promise.all([
                    searchHospitals(query, hospitalCategory),
                    dottedQuery ? searchHospitals(dottedQuery, hospitalCategory) : Promise.resolve({ data: [] }),
                ]);

                const list1 = Array.isArray(res1?.data) ? res1.data : [];
                const list2 = Array.isArray(res2?.data) ? res2.data : [];

                // Merge, deduplicate by name
                const seen = new Set();
                const merged = [...list1, ...list2].filter(h => {
                    const key = (h.name || h).toLowerCase();
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                });

                // Flexible prefix match: strip non-alphanumeric from both sides
                // so "AJ" matches "A.J Hospital", "St Mary" matches "St. Mary's Hospital"
                const normalize = (str) => str.toLowerCase().replace(/[^a-z0-9]/g, "");
                const filtered = merged.filter(h =>
                    normalize(h.name || h).includes(normalize(query))
                );

                setHospitalSuggestions(filtered);
                setShowHospitalDropdown(filtered.length > 0);
            } catch {
                setHospitalSuggestions([]);
                setShowHospitalDropdown(false);
                setHospitalSearchError(t.hospitalSearchFailed);
            } finally {
                setHospitalLoading(false);
            }
        }, 350);
    };

    const handleHospitalSelect = (hospital) => {
        setMedicalCertificateIssuedBy(hospital.name || hospital);
        setHospitalId(hospital.id || "");
        setHospitalSuggestions([]);
        setShowHospitalDropdown(false);
        setErrors(prev => ({ ...prev, medicalCertificateIssuedBy: "" }));
    };

    const handleSaveDraft = async () => {
        setDraftSaving(true);
        setDraftError("");
        try {
            const fd = new FormData();
            fd.append("risk_fund_claim_type_id", btoa("1"));
            fd.append("loan_number", loan.loanNo);
            if (draftId) fd.append("risk_fund_claim_id", draftId);
            if (diseaseCategory) fd.append("disease_id", diseaseCategory);
            if (hospitalCategory) fd.append("hospital_category_id", btoa(String(hospitalCategory)));
            if (hospitalId) fd.append("hospital_id", btoa(String(hospitalId)));
            if (dateOfMedicalCertificate) fd.append("medical_certificate_date", dateOfMedicalCertificate);
            if (dateOfIllness) fd.append("illness_start_date", dateOfIllness);
            if (medicalCertificateFile) fd.append("medical_certificate_file", medicalCertificateFile);
            if (dobProofFile) fd.append("dob_proof_file", dobProofFile);
            if (dobProofDocType) {
                const selectedIdProof = idProofTypes.find(c => String(c.id) === String(dobProofDocType));
                const idProofName = selectedIdProof ? (t.idProofNames?.[selectedIdProof.name] || selectedIdProof.name) : "";
                if (idProofName) fd.append("id_proof_name", idProofName);
            }
            if (dobProofDocFile) fd.append("id_proof_file", dobProofDocFile);
            if (anotherDocName) fd.append("other_document_name", anotherDocName);
            if (anotherDocFile) fd.append("other_document_file", anotherDocFile);

            const res = await saveDraft(fd);
            const id = res?.data?.risk_fund_claim_id;
            if (id) {
                setDraftId(id);
            }
            setDraftSaved(true);
        } catch (err) {
            setDraftError(err.message || t.saveDraftError);
        } finally {
            setDraftSaving(false);
        }
    }

    useEffect(() => {
        setLoadingDiseaseCategories(true);
        loadDiseases()
            .then(res => {
                const list = Array.isArray(res?.data) ? res.data : [];
                setDiseaseCategories(list);
            })
            .catch(() => setDiseaseCategories([]))
            .finally(() => setLoadingDiseaseCategories(false));
    }, []);

    useEffect(() => {
        setLoadingHospitalCategories(true);
        loadHospitalCategories()
            .then(res => {
                const list = Array.isArray(res?.data) ? res.data : [];
                setHospitalCategories(list);
                if (list.length > 0 && state?.freshClaim) {
                    setHospitalCategory(list[0].id ?? list[0].name ?? "");
                }
            })
            .catch(() => setHospitalCategories([]))
            .finally(() => setLoadingHospitalCategories(false));          
    }, []);

    useEffect(()=>{
        setLoadingIdProofTypes(true);
        fetchIdProofs().then(
            res=>{
                const list = Array.isArray(res?.data) ? res.data : [];
                setIdProofTypes(list)
            })
            .catch(() => setIdProofTypes([]))
            .finally(()=> setLoadingIdProofTypes(false))
    },[])

    useEffect(() => { setDeclared(false); }, [diseaseCategory, dateOfIllness, medicalCertificateIssuedBy, dateOfMedicalCertificate, medicalCertificateFile, dobProofFile]);

    const allFieldsFilled = diseaseCategory && dateOfIllness && medicalCertificateIssuedBy.trim() && hospitalId && dateOfMedicalCertificate && medicalCertificateFile && dobProofFile

    const handleCheckboxClick = () => {
        const newErrors = {
            diseaseCategory: !diseaseCategory ? t.diseaseCategoryRequired : "",
            dateOfIllness: !dateOfIllness ? t.dateOfIllnessRequired : "",
            medicalCertificateIssuedBy: !medicalCertificateIssuedBy.trim() ? t.medicalCertIssuedByRequired
                : !hospitalId ? t.selectHospitalFromSuggestions : "",
            dateOfMedicalCertificate: !dateOfMedicalCertificate ? t.dateOfMedCertRequired : "",
            medicalCertificateFile: !medicalCertificateFile ? t.uploadMedCertRequired : "",
            dobProofFile: !dobProofFile ? t.uploadDobProofRequired : "",
        }
        if (dateOfMedicalCertificate && dateOfIllness && dateOfMedicalCertificate < dateOfIllness) {
            newErrors.dateOfMedicalCertificate = t.medCertDateBeforeIllness
        }
        const hasErrors = Object.values(newErrors).some(e => e)
        if (hasErrors) {
            setErrors(newErrors)
            return
        }
        setDeclared(prev => !prev)
    }

    const handlePreview = () => {
        if (!declared) {
            setDeclarationError(t.pleaseCheckDeclaration || "Please check the declaration to proceed.");
            return;
        }
        setDeclarationError("");
        const hospitalCategoryName = hospitalCategories.find(c => String(c.id) === String(hospitalCategory))?.name || hospitalCategory;
        const diseaseCategoryName = diseaseCategories.find(c => String(c.id) === String(diseaseCategory))?.name || diseaseCategory;
        const selectedIdProof = idProofTypes.find(c => String(c.id) === String(dobProofDocType));
        const dobProofDocTypeName = selectedIdProof ? (t.idProofNames?.[selectedIdProof.name] || selectedIdProof.name) : "";
        if (draftId) sessionStorage.setItem("previewDraftId", draftId);
        const claimState = { diseaseCategory, diseaseCategoryName, dateOfIllness, hospitalCategory, hospitalCategoryName, hospitalId, medicalCertificateIssuedBy, dateOfMedicalCertificate, dobProofDocType, dobProofDocTypeName, anotherDocName };
        const filesState = { medicalCertificateFile, dobProofFile, dobProofDocFile, anotherDocFile };
        navigate("/previewDiseaseClaim", { state: { loan, claim: claimState, files: filesState, draftId } });
    }

    return(
        <>
        <Header/>

         {/*-- ==== Scroll up and prograss start ==== */}
    <div id="scrollUp">
        <svg className="arrowup" viewBox="0 0 24 24" width="18" height="18">
            <path d="M13 7.828V20h-2V7.828l-5.364 5.364-1.414-1.414L12 4l7.778 7.778-1.414 1.414L13 7.828z" fill="#fff"></path>
        </svg>
        <svg className="scrollprogress" width="40" height="40">
            <circle className="progress-circle" cx="20" cy="20" r="18" strokeWidth="2" fill="none" stroke="#fff" strokeDasharray="113.1" strokeDashoffset="113.1"></circle>
        </svg>
    </div>
    {/*-- ==== Scroll up and prograss Ends ==== */}


    {/* ==== Account Creation Page Banner Section Start ==== */}
    <section className="rs-page-banner rs-faq-page-banner pt-30 pb-30 mt-140">
        <div className="container">
            <div className="row">
                <div className="col-lg-7">
                    <div className="rs-page-banner__content">
                        <h1 className="title">{t.diseaseClaimTitle}</h1>
                    </div>
                </div>
            </div>
        </div>
    </section>
    {/*- ==== Account Creation Page Banner Section Ends ==== */}


    {/*-- ====Page Section Start ==== */}
    <section className="rs-faq-page pt-50 pb-120">
        <div className="container">

            <div className="row justify-content-center">
                <div className="col-lg-12">

                    {/*--loan details--*/}
                    <h5 className="mb-2">{t.loanDetails}</h5>
                    <div className="list-type mb-5">
                        <div className="row">
                            <div className="col-md-3 mb-2">
                                <p>{t.loaneeFullName}</p>
                                <h4>{loan.name}</h4>
                            </div>
                            <div className="col-md-3 mb-2">
                                <p>{t.aadhaarNumber}</p>
                                <h4>{maskAadhaar(loan.aadhaar)}</h4>
                            </div>
                            <div className="col-md-3 mb-2">
                                <p>{t.mobileNumber}</p>
                                <h4>{loan.mobile}</h4>
                            </div>
                            <div className="col-md-3 mb-2">
                                <p>{t.loanNumber}</p>
                                <h4>{loan.loanNo}</h4>
                            </div>
                            <div className="col-md-3 mb-2">
                                <p>{t.loanAmount}</p>
                                <h4>{loan.amount}</h4>
                            </div>
                            <div className="col-md-3 mb-2">
                                <p>{t.societyMembership}</p>
                                <h4>{loan.membership}</h4>
                            </div>
                            <div className="col-md-3 mb-2">
                                <p>{t.societyName}</p>
                                <h4>{loan.society}</h4>
                            </div>
                             <div className="col-md-3 mb-2">
                                <p>{t.societyAddress}</p>
                                <h4>{loan.societyAddress}</h4>
                            </div>
                            <div className="col-md-3 mb-2">
                                <p>{t.dateOfBirth}</p>
                                <h4>{loan.dob}</h4>
                            </div>
                            <div className="col-md-3 mb-2">
                                <p>{t.birthCertificate}</p>
                                <h4>{loan.birthCertificateNumber}</h4>
                            </div>
                            <div className="col-md-3 mb-2">
                                <a href={getDobVerifyUrl()} target="_blank" rel="noopener noreferrer" style={{ color: "#1a5c4a", fontSize: "0.9rem" }}>
                                    <i className="fa fa-external-link me-1"></i> {t.view}
                                </a>
                            </div>
                        </div>
                    </div>
                    {/*--loan details--*/}
                    
                    {/*--Disease claim details--*/}
                    <h5 className="mb-2">{t.diseaseClaimDetails}</h5>
                    <div className="list-type mb-5">
                        <div className="row">

                            {/* Disease Category */}
                            <div className="col-md-6 mb-3">
                                <div className="form-group">
                                    <label className="form-label">{t.diseaseCategory} <span className="text-danger">*</span></label>
                                    <select
                                        className={`form-control ${errors.diseaseCategory ? "is-invalid" : ""}`}
                                        value={diseaseCategory}
                                        disabled={loadingDiseaseCategories}
                                        onChange={e => { setDiseaseCategory(e.target.value); if (errors.diseaseCategory) setErrors(prev => ({ ...prev, diseaseCategory: "" })) }}
                                    >
                                        {loadingDiseaseCategories
                                            ? <option value="">{t.loading}</option>
                                            : <>
                                                <option value="">{t.selectDiseaseCategory}</option>
                                                {diseaseCategories.map(cat => (
                                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                ))}
                                              </>
                                        }
                                    </select>
                                    {errors.diseaseCategory && <div className="text-danger small mt-1">{errors.diseaseCategory}</div>}
                                </div>
                            </div>

                            {/* Date of Illness */}
                            <div className="col-md-6 mb-3">
                                <div className="form-group">
                                    <label className="form-label">{t.dateOfIllness} <span className="text-danger">*</span></label>
                                    <DatePicker
                                        value={dateOfIllness}
                                        onChange={val => {
                                            setDateOfIllness(val);
                                            const certError = dateOfMedicalCertificate && dateOfMedicalCertificate < val
                                                ? t.medCertDateBeforeIllness
                                                : "";
                                            setErrors(prev => ({ ...prev, dateOfIllness: "", dateOfMedicalCertificate: certError }));
                                        }}
                                        placeholder="DD/MM/YYYY"
                                        className={errors.dateOfIllness ? "is-invalid" : ""}
                                    />
                                    {errors.dateOfIllness && <div className="text-danger small mt-1">{errors.dateOfIllness}</div>}
                                </div>
                            </div>

                            {/* Medical Certificate Issued by — heading + hospital category + search on same line */}
                            <div className="col-md-12 mb-1">
                                <label className="form-label fw-semibold">{t.medicalCertIssuedBy} <span className="text-danger">*</span></label>
                            </div>
                            <div className="col-md-12 mb-3">
                                <div className="row g-2 align-items-start">
                                    {/* Hospital Category */}
                                    <div className="col-md-2">
                                        <div className="form-group" style={{ marginBottom: 0 }}>
                                        <select
                                            className="form-control"
                                            style={{ height: "38px" }}
                                            value={hospitalCategory}
                                            disabled={loadingHospitalCategories}
                                            onChange={e => setHospitalCategory(e.target.value)}
                                        >
                                            {loadingHospitalCategories
                                                ? <option value="">{t.loading}</option>
                                                : hospitalCategories.map(cat => (
                                                    <option key={cat.id ?? cat.name} value={cat.id ?? cat.name}>
                                                        {cat.name}
                                                    </option>
                                                ))
                                            }
                                        </select>
                                        </div>
                                    </div>
                                    {/* Hospital Search */}
                                    <div className="col-md-10">
                                        <div ref={hospitalDropdownRef} style={{ position: "relative" }}>
                                            <div className={`input-group ${errors.medicalCertificateIssuedBy ? "is-invalid" : ""}`}>
                                                <span className="input-group-text bg-white border-end-0">
                                                    {hospitalLoading
                                                        ? <span className="spinner-border spinner-border-sm text-secondary" style={{ width: "14px", height: "14px" }}></span>
                                                        : <i className="fa fa-search"></i>
                                                    }
                                                </span>
                                                <input
                                                    type="text"
                                                    className={`form-control border-start-0 ${errors.medicalCertificateIssuedBy ? "is-invalid" : ""}`}
                                                    placeholder={t.searchHospital}
                                                    value={medicalCertificateIssuedBy}
                                                    onChange={e => handleHospitalSearch(e.target.value)}
                                                    autoComplete="off"
                                                />
                                            </div>
                                            {showHospitalDropdown && (
                                                <ul style={{
                                                    position: "absolute",
                                                    top: "100%",
                                                    left: 0,
                                                    right: 0,
                                                    zIndex: 1000,
                                                    background: "#fff",
                                                    border: "1px solid #ddd",
                                                    borderRadius: "4px",
                                                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                                    maxHeight: "220px",
                                                    overflowY: "auto",
                                                    margin: 0,
                                                    padding: 0,
                                                    listStyle: "none",
                                                }}>
                                                    {hospitalSuggestions.map((hospital, idx) => (
                                                        <li
                                                            key={idx}
                                                            onMouseDown={() => handleHospitalSelect(hospital)}
                                                            style={{
                                                                padding: "10px 14px",
                                                                cursor: "pointer",
                                                                fontSize: "14px",
                                                                borderBottom: idx < hospitalSuggestions.length - 1 ? "1px solid #f0f0f0" : "none",
                                                            }}
                                                            onMouseEnter={e => e.currentTarget.style.background = "#f5f5f5"}
                                                            onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                                                        >
                                                            {hospital.name || hospital}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                        {errors.medicalCertificateIssuedBy && <div className="text-danger small mt-1">{errors.medicalCertificateIssuedBy}</div>}
                                        {hospitalSearchError && <div className="text-danger small mt-1">{hospitalSearchError}</div>}
                                    </div>
                                </div>
                            </div>

                            {/* Date of Medical Certificate */}
                            <div className="col-md-6 mb-3">
                                <div className="form-group">
                                    <label className="form-label">{t.medicalCertDate} <span className="text-danger">*</span></label>
                                    <DatePicker
                                        value={dateOfMedicalCertificate}
                                        onChange={val => {
                                            setDateOfMedicalCertificate(val);
                                            const certError = dateOfIllness && val < dateOfIllness
                                                ? t.medCertDateBeforeIllness
                                                : "";
                                            setErrors(prev => ({ ...prev, dateOfMedicalCertificate: certError }));
                                        }}
                                        placeholder="DD/MM/YYYY"
                                        className={errors.dateOfMedicalCertificate ? "is-invalid" : ""}
                                    />
                                    {errors.dateOfMedicalCertificate && <div className="text-danger small mt-1">{errors.dateOfMedicalCertificate}</div>}
                                </div>
                            </div>

                        {/*--Documents Upload--*/}
                   <h5 className="mb-2">{t.documentsUpload} <span className="text-danger">*</span></h5>
                    <div className="list-type mb-4">
                        <div className="row">
                            <div className="col-md-6">
                                <FileUpload label={t.uploadMedicalCertificate} initialFile={medicalCertificateFile} onChange={f => { setMedicalCertificateFile(f); if (f) setErrors(prev => ({ ...prev, medicalCertificateFile: "" })) }} />
                                {errors.medicalCertificateFile && <div className="text-danger small mt-1">{errors.medicalCertificateFile}</div>}
                            </div>
                            <div className="col-md-6">
                                <FileUpload label={t.uploadDobProof} initialFile={dobProofFile} onChange={f => { setDobProofFile(f); if (f) setErrors(prev => ({ ...prev, dobProofFile: "" })) }} />
                                {errors.dobProofFile && <div className="text-danger small mt-1">{errors.dobProofFile}</div>}
                            </div>
                        </div>
                    </div>
                    {/*--Documents Upload--*/}

                    {/*--Additional Documents--*/}
                    <h5 className="mb-2">{t.additionalDocuments}</h5>
                    <div className="list-type mb-5">
                        <div className="row">
                            {/* Left: Dropdown + ID proof upload | Right: Text input + Another doc upload */}
                            <div className="col-md-6">
                                <label className="form-label">{t.chooseValidDocument}</label>
                                <div style={{ position: "relative" }}>
                                    <select
                                        className="form-control"
                                        value={dobProofDocType}
                                        onChange={e => setDobProofDocType(e.target.value)}
                                        disabled={loadingIdProofTypes}
                                        style={{ appearance: "none", WebkitAppearance: "none", paddingRight: "2rem" }}
                                    >
                                        {loadingIdProofTypes
                                            ? <option value="">{t.loading}</option>
                                            : <>
                                                <option value="">{t.chooseValidDocument}</option>
                                                {idProofTypes.map(type => (
                                                    <option key={type.id} value={type.id}>{t.idProofNames?.[type.name] || type.name}</option>
                                                ))}
                                              </>
                                        }
                                    </select>
                                    <i className="fa fa-chevron-down" style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "#6c757d", fontSize: "0.8rem" }}></i>
                                </div>
                                <div className="mt-3">
                                    <FileUpload label={t.uploadIdProofDoc} initialFile={dobProofDocFile} onChange={f => setDobProofDocFile(f)} />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">{t.anotherDocument}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={t.anotherDocumentPlaceholder}
                                    value={anotherDocName}
                                    onChange={e => setAnotherDocName(e.target.value)}
                                />
                                <div className="mt-3">
                                    <FileUpload label={t.uploadAnotherDoc} initialFile={anotherDocFile} onChange={f => setAnotherDocFile(f)} />
                                </div>
                            </div>
                        </div>
                    </div>
                    {/*--Additional Documents--*/}
                        </div>
                    </div>
                    {/*--Disease claim details--*/}
                    

                   {/*--declaration--*/}
                    <div className="card info-status p-2 mt-3">
                        <div className="row align-items-center justify-content-between">
                            <div className="col-auto">
                                <div onClick={handleCheckboxClick} style={{display:"inline-block", cursor: allFieldsFilled ? "pointer" : "not-allowed"}}>
                                    <input type="checkbox" className="form-check-input" checked={declared} onChange={()=>{}} disabled={!allFieldsFilled} style={{accentColor: "#28a745", pointerEvents:"none"}}/>
                                </div>
                            </div>
                            <div className="col">
                                <span className="form-check-label">{t.diseaseClaimDeclText}</span>
                            </div>
                        </div>
                    </div>
                    {/*--declaration--*/}
                    {declarationError && (
                        <div className="text-danger small mt-2">{declarationError}</div>
                    )}

                    {draftError && (
                        <div className="alert alert-danger mt-3">{draftError}</div>
                    )}
                    <div className="row mt-3">
                        <div className="col-md-12 text-end">
                            <button className="btn btn-secondary me-3" onClick={() => navigate("/claimLoaneeIdentification")}><i className="fa fa-angle-left me-2"></i>{t.back}</button>
                            <button className="btn btn-secondary me-3" disabled={draftSaving} onClick={handleSaveDraft}>
                                {draftSaving
                                    ? <><span className="spinner-border spinner-border-sm me-2"></span>{t.saving}</>
                                    : <><i className="fa fa-save me-2"></i>{t.saveDraft}</>
                                }
                            </button>
                            <button className="btn btn-primary me-3" onClick={handlePreview}><i className="fa fa-check-circle me-2"></i> {t.previewClaim}</button>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    </section>
    {/*-- ====Page Section Ends ==== --*/}

        <Footer/>

        {draftSaved && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div className="card text-center p-5" style={{ maxWidth: "420px", width: "90%" }}>
                    <div className="mb-3">
                        <i className="fa fa-check-circle" style={{ fontSize: "3.5rem", color: "#25826a" }}></i>
                    </div>
                    <h4 className="mb-2">{t.draftSavedTitle}</h4>
                    <p className="text-muted mb-4">{t.draftSavedDesc}</p>
                    <button className="btn btn-primary" onClick={() => { setDraftSaved(false); navigate("/userDashboard"); }}>{t.ok}</button>
                </div>
            </div>
        )}
        </>
    )
}

export default DiseaseClaim
